import React, { useEffect, useState } from 'react';
import { FiSearch, FiCheck, FiX, FiTrash2 } from 'react-icons/fi';
import { adminApi } from '../../services/adminApi';

function AdminInvestors() {
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (searchDebounced) params.search = searchDebounced;
    adminApi.getInvestors(params)
      .then((res) => {
        if (res.success) {
          setInvestors(res.data || []);
          setError(null);
        } else setError(res.message || 'Failed to load');
      })
      .catch((err) => {
        setError(err.message || 'Failed to load');
        setInvestors([]);
      })
      .finally(() => setLoading(false));
  }, [searchDebounced]);

  const handleStatus = async (id, status) => {
    setUpdating(id);
    try {
      const res = await adminApi.updateInvestorStatus(id, status);
      if (res.success) {
        setInvestors((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently delete investor "${name || id}"?`)) return;
    setUpdating(id);
    try {
      const res = await adminApi.deleteInvestor(id);
      if (res.success) {
        setInvestors((prev) => prev.filter((i) => i.id !== id));
        setError(null);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header admin-page-header-row">
        <div>
          <h1>Investor Management</h1>
          <p>Manage investor registrations (WhatsApp & website). Approve to send them farm opportunity updates.</p>
        </div>
      </div>
      <div className="admin-toolbar">
        <div className="admin-search">
          <FiSearch className="admin-search-icon" />
          <input
            type="search"
            placeholder="Search investors..."
            className="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="admin-cards-grid">
        {loading ? (
          <div className="admin-loading admin-loading-full" />
        ) : error ? (
          <p className="admin-error">{error}</p>
        ) : investors.length === 0 ? (
          <p className="admin-empty">No investors found</p>
        ) : (
          investors.map((i) => (
            <div key={i.id} className="admin-card">
              <div className="admin-card-header">
                <span className={`admin-badge admin-badge-${i.status === 'active' ? 'active' : i.status === 'inactive' ? 'rejected' : 'pending'}`}>
                  {i.status}
                </span>
                <code className="admin-card-id">I{String(i.id).padStart(3, '0')}</code>
              </div>
              <h3 className="admin-card-title">{i.full_name}</h3>
              <p className="admin-card-meta">{i.phone || '-'}</p>
              <p className="admin-card-meta" style={{ fontSize: '0.85rem' }}>{i.email || '-'}</p>
              <p className="admin-card-desc">{i.interests || 'No interests specified'}</p>
              <div className="admin-card-actions">
                {i.status === 'pending' && (
                  <>
                    <button
                      type="button"
                      className="admin-btn-icon admin-btn-success"
                      title="Activate (receive updates)"
                      onClick={() => handleStatus(i.id, 'active')}
                      disabled={updating === i.id}
                    >
                      <FiCheck />
                    </button>
                    <button
                      type="button"
                      className="admin-btn-icon admin-btn-danger"
                      title="Deactivate"
                      onClick={() => handleStatus(i.id, 'inactive')}
                      disabled={updating === i.id}
                    >
                      <FiX />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="admin-btn-icon admin-btn-danger"
                  title="Delete investor"
                  onClick={() => handleDelete(i.id, i.full_name)}
                  disabled={updating === i.id}
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminInvestors;
